import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import CLOSE from "../../../assets/CLOSE.svg";
import downIcon from "../../../assets/down-icon.svg";
import searchIcon from "../../../assets/search-new-icon.svg";
import closeIcon from "../../../assets/crossIcon.svg";
import { Country, State } from "country-state-city";
import { FilterDropdown } from "./FilterDropdown";
import { ProcedureFlowServices } from "../../../services/ProcedureFlowServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { getUser } from "../../../config/user";

const INDIAN_STATE_LIST = State.getStatesOfCountry("IN");
const INDIAN_STATES = INDIAN_STATE_LIST.map((s) => s.name);

function resolveIndianStateCode(stateId) {
  const raw = String(stateId ?? "").trim();
  if (!raw) return "";
  const byName = INDIAN_STATE_LIST.find((s) => s.name === raw);
  if (byName) return byName.isoCode;
  const byIso = INDIAN_STATE_LIST.find((s) => s.isoCode === raw);
  return byIso ? byIso.isoCode : raw;
}

function normalizeStateStatus(s) {
  const raw = String(s ?? "").trim().toLowerCase();
  return raw === "inactive" ? "inactive" : "active";
}

function stateEntryFromApi(s, userId) {
  const now = new Date().toISOString();
  if (s != null && typeof s === "object") {
    const name = s.stateId != null ? String(s.stateId).trim() : "";
    const code =
      s.stateCode != null && String(s.stateCode).trim() !== ""
        ? String(s.stateCode).trim()
        : resolveIndianStateCode(name);
    return {
      stateId: name,
      stateCode: code,
      createdAt: s.createdAt || now,
      createdBy: s.createdBy ?? userId ?? "",
      status: normalizeStateStatus(s.status),
    };
  }
  const name = String(s ?? "").trim();
  return {
    stateId: name,
    stateCode: resolveIndianStateCode(name),
    createdAt: now,
    createdBy: userId ?? "",
    status: "active",
  };
}

function newActiveStateEntry(stateName, userId) {
  const now = new Date().toISOString();
  return {
    stateId: stateName,
    stateCode: resolveIndianStateCode(stateName),
    createdAt: now,
    createdBy: userId ?? "",
    status: "active",
  };
}

function stateEntriesSnapshot(entries) {
  return [...entries]
    .map((e) => `${e.stateId}::${e.status}`)
    .sort()
    .join("|");
}

const PROCEDURE_COST_MAPPING_OPTIONS = [
  { value: "STATE", label: "State" },
  { value: "NATIONAL", label: "Country" },
  { value: "HOSPITAL", label: "Hospital" },
  { value: "GLOBAL", label: "Global" },
];

/**
 * Same Edit Scheme form as Manage Scheme page, shown as a modal overlay.
 * @param {boolean} open
 * @param {function} onClose
 * @param {object|null} scheme — scheme row object (id, name, stateIds, procedureCostMapping, …)
 * @param {function} onSaved — called after successful save (parent refetches schemes / prefills mapping)
 */
export default function EditSchemeModal({ open, onClose, scheme, onSaved }) {
  const [currentScheme, setCurrentScheme] = useState({});
  const [initialSchemeForEdit, setInitialSchemeForEdit] = useState(null);
  const [schemeStateEntries, setSchemeStateEntries] = useState([]);
  const [stateDropdownValue, setStateDropdownValue] = useState("");
  const [stateMappingModal, setStateMappingModal] = useState({
    open: false,
    action: null,
    stateName: null,
  });
  const [stateMappingBusy, setStateMappingBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const schemeStateIdsKey = useMemo(
    () => JSON.stringify(scheme?.stateIds ?? null),
    [scheme?.stateIds]
  );

  useEffect(() => {
    if (!open || !scheme?.id) return;
    const userId = getUser()?.user?.id ?? getUser()?.id ?? "";
    const stateIds = scheme.stateIds ?? [];
    const entries = Array.isArray(stateIds)
      ? stateIds.map((s) => stateEntryFromApi(s, userId))
      : [];
    setSchemeStateEntries(entries);
    setStateDropdownValue("");
    const schemeData = {
      id: scheme.id,
      name: scheme.name,
      status: scheme.status,
      details: scheme.details,
      procedureCostMapping: scheme.procedureCostMapping || "STATE",
      schemeCountryId: scheme.schemeCountryId || "IN",
      stateIds: scheme.stateIds ?? [],
      childProcedureMapping: scheme.childProcedureMapping ?? false,
      createdAt: scheme.createdAt,
      updatedAt: scheme.updatedAt,
      createdBy: scheme.createdBy,
      updatedBy: scheme.updatedBy,
    };
    setCurrentScheme(schemeData);
    setInitialSchemeForEdit(schemeData);
    setSaveError(null);
  }, [open, scheme?.id, schemeStateIdsKey]);

  const buildStateIdsPayload = (entries) =>
    entries.map((e) => ({
      stateId: e.stateId,
      stateCode: e.stateCode || resolveIndianStateCode(e.stateId),
      createdAt: e.createdAt,
      createdBy: e.createdBy,
      status: e.status,
    }));

  const syncCurrentSchemeStateIdsFromEntries = (entries) => {
    const ids = buildStateIdsPayload(entries);
    Promise.resolve().then(() => {
      setCurrentScheme((prev) => ({ ...prev, stateIds: ids }));
    });
  };

  const markStateInactiveLocal = (stateName) => {
    setSchemeStateEntries((prev) => {
      const next = prev.map((e) =>
        e.stateId === stateName ? { ...e, status: "inactive" } : e
      );
      syncCurrentSchemeStateIdsFromEntries(next);
      return next;
    });
  };

  const markStateActiveLocal = (stateName) => {
    const uid = getUser()?.user?.id ?? getUser()?.id ?? "";
    const now = new Date().toISOString();
    setSchemeStateEntries((prev) => {
      const next = prev.map((e) =>
        e.stateId === stateName
          ? {
              ...e,
              status: "active",
              createdAt: e.createdAt || now,
              createdBy: e.createdBy || uid,
            }
          : e
      );
      syncCurrentSchemeStateIdsFromEntries(next);
      return next;
    });
  };

  const closeStateMappingModal = () =>
    setStateMappingModal({ open: false, action: null, stateName: null });

  const handleConfirmStateMapping = async () => {
    const stateName = stateMappingModal.stateName;
    const action = stateMappingModal.action;
    if (!stateName || !currentScheme?.id || !action) {
      closeStateMappingModal();
      return;
    }
    setStateMappingBusy(true);
    try {
      if (action === "deactivate") {
        await ProcedureFlowServices.updateSchemeStateMapping({
          schemeId: Number(currentScheme.id),
          removeStates: [stateName],
        });
        toast.success("State set to inactive.");
        markStateInactiveLocal(stateName);
      } else {
        await ProcedureFlowServices.updateSchemeStateMapping({
          schemeId: Number(currentScheme.id),
          addStates: [stateName],
        });
        toast.success("State activated.");
        markStateActiveLocal(stateName);
      }
      closeStateMappingModal();
    } catch (err) {
      handleApiErrorResponses(err);
      toast.error(
        action === "deactivate"
          ? "Failed to update state mapping."
          : "Failed to activate state."
      );
    } finally {
      setStateMappingBusy(false);
    }
  };

  const handleSave = async () => {
    const name = (currentScheme?.name ?? "").trim();
    if (!name) {
      setSaveError("Scheme name is required.");
      return;
    }
    setSaveError(null);
    setSaving(true);
    try {
      const procedureCostMapping = currentScheme.procedureCostMapping || "STATE";
      const stateIds =
        procedureCostMapping === "STATE"
          ? buildStateIdsPayload(schemeStateEntries)
          : undefined;
      await ProcedureFlowServices.updateSchemeType({
        id: currentScheme.id,
        name,
        procedureCostMapping,
        schemeCountryId: (currentScheme.schemeCountryId ?? "").trim() || "IN",
        childProcedureMapping: currentScheme.childProcedureMapping ?? false,
        ...(procedureCostMapping === "STATE" && { stateIds: stateIds ?? [] }),
      });
      toast.success("Scheme updated successfully.");
      if (typeof onSaved === "function") {
        await onSaved();
      }
      onClose();
    } catch (err) {
      console.error("Failed to update scheme", err);
      handleApiErrorResponses(err);
      setSaveError(err?.response?.data?.message || "Failed to update scheme. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const costMapping = currentScheme?.procedureCostMapping ?? "STATE";
  const initialCostMapping = initialSchemeForEdit?.procedureCostMapping ?? "STATE";
  const uidForCompare = getUser()?.user?.id ?? getUser()?.id ?? "";
  const initialEntries = Array.isArray(initialSchemeForEdit?.stateIds)
    ? initialSchemeForEdit.stateIds.map((s) => stateEntryFromApi(s, uidForCompare))
    : [];
  const statesChanged =
    costMapping === "STATE" &&
    stateEntriesSnapshot(schemeStateEntries) !== stateEntriesSnapshot(initialEntries);
  const hasChanges =
    !initialSchemeForEdit ||
    (currentScheme?.name ?? "").trim() !== (initialSchemeForEdit?.name ?? "").trim() ||
    costMapping !== initialCostMapping ||
    (currentScheme?.schemeCountryId ?? "").trim() !== (initialSchemeForEdit?.schemeCountryId ?? "").trim() ||
    Boolean(currentScheme?.childProcedureMapping) !== Boolean(initialSchemeForEdit?.childProcedureMapping) ||
    statesChanged;

  if (!open || typeof document === "undefined") return null;

  const modal = (
    <>
      <div
        className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 backdrop-blur-sm px-4 py-8 overflow-y-auto"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="relative w-full max-w-lg bg-white rounded-2xl sm:rounded-3xl border border-[#E0E0E0] p-6 sm:p-8 shadow-xl max-h-[90vh] overflow-y-auto"
          role="dialog"
          onClick={(e) => e.stopPropagation()}
          aria-modal="true"
          aria-labelledby="edit-scheme-modal-title"
        >
        <div className="flex items-center justify-between mb-6">
          <h2 id="edit-scheme-modal-title" className="font-lufga text-[28px] sm:text-[32px] font-normal">
            Edit Scheme
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100 border-none bg-transparent cursor-pointer"
          >
            <img src={CLOSE} alt="close" className="w-6 h-6" />
          </button>
        </div>

        <div className="relative mb-4">
          <input
            type="text"
            placeholder="Name of the Scheme"
            value={currentScheme?.name || ""}
            onChange={(e) => {
              setCurrentScheme({ ...currentScheme, name: e.target.value });
              setSaveError(null);
            }}
            className="w-full h-[70px] px-6 pr-36 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px]"
          />
          {(currentScheme?.name || "").length > 0 && (
            <span className="absolute right-6 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
              Scheme Name
            </span>
          )}
        </div>

        <div className="relative mb-4">
          <select
            value={currentScheme?.procedureCostMapping || "STATE"}
            onChange={(e) =>
              setCurrentScheme({
                ...currentScheme,
                procedureCostMapping: e.target.value,
              })
            }
            className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
          >
            {PROCEDURE_COST_MAPPING_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <img
            src={downIcon}
            alt=""
            className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          />
          <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Cost Mapping
          </span>
        </div>

        {(currentScheme?.procedureCostMapping || "STATE") === "STATE" && (
          <div className="mb-4">
            <div className="w-full">
              <FilterDropdown
                placeholder="Select State"
                options={INDIAN_STATES}
                value={stateDropdownValue}
                setValue={(state) => {
                  if (!state) return;
                  const userId = getUser()?.user?.id ?? getUser()?.id ?? "";
                  const existing = schemeStateEntries.find((e) => e.stateId === state);
                  if (existing?.status === "active") {
                    setStateDropdownValue("");
                    return;
                  }
                  if (existing?.status === "inactive") {
                    if (currentScheme?.id) {
                      setStateMappingModal({
                        open: true,
                        action: "activate",
                        stateName: state,
                      });
                    }
                    setStateDropdownValue("");
                    return;
                  }
                  setSchemeStateEntries((prev) => [...prev, newActiveStateEntry(state, userId)]);
                  setStateDropdownValue("");
                }}
                enableSearch={true}
                fullWidth={true}
                clearValue=""
                variant="panel"
                keepOpenOnSelect={true}
                selectedItems={schemeStateEntries
                  .filter((e) => e.status === "active")
                  .map((e) => e.stateId)}
              />
              {schemeStateEntries.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2 items-center">
                  {[...schemeStateEntries]
                    .sort((a, b) => {
                      if (a.status !== b.status) return a.status === "active" ? -1 : 1;
                      return a.stateId.localeCompare(b.stateId);
                    })
                    .map((entry) => {
                      const isActive = entry.status === "active";
                      return (
                        <span
                          key={entry.stateId}
                          dir="ltr"
                          className={`inline-flex flex-row items-center gap-2 px-3 py-1.5 rounded-full font-lufga text-xs font-medium max-w-[260px] ${
                            isActive
                              ? "bg-[#5AC69F]/25 text-[#1B1B1B] border border-[#5AC69F]"
                              : "bg-gray-100 text-gray-600 border border-gray-300"
                          }`}
                          title={
                            isActive ? entry.stateId : `${entry.stateId} (inactive)`
                          }
                        >
                          {!isActive && (
                            <button
                              type="button"
                              disabled={stateMappingBusy}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentScheme?.id) {
                                  setStateMappingModal({
                                    open: true,
                                    action: "activate",
                                    stateName: entry.stateId,
                                  });
                                }
                              }}
                              className="inline-flex shrink-0 items-center justify-center w-[22px] h-[22px] rounded-full border border-[#5AC69F] bg-white text-[#5AC69F] p-0 cursor-pointer hover:bg-[#5AC69F]/10 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Activate this state"
                              aria-label={`Activate ${entry.stateId}`}
                            >
                              <svg
                                width="12"
                                height="12"
                                viewBox="0 0 12 12"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden
                              >
                                <path
                                  d="M2.5 6.2L5.1 8.8L9.5 3.5"
                                  stroke="currentColor"
                                  strokeWidth="1.4"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                          <span className="min-w-0 flex-1 truncate">{entry.stateId}</span>
                          {isActive ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (currentScheme?.id) {
                                  setStateMappingModal({
                                    open: true,
                                    action: "deactivate",
                                    stateName: entry.stateId,
                                  });
                                }
                              }}
                              className="flex items-center justify-center w-4 h-4 rounded-full hover:bg-[#5AC69F]/80 text-[#1B1B1B] text-[10px] font-bold cursor-pointer bg-transparent border-none leading-none shrink-0"
                            >
                              &#x2715;
                            </button>
                          ) : null}
                        </span>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        <CountryDropdown
          value={currentScheme?.schemeCountryId ?? ""}
          onChange={(code) => {
            setCurrentScheme({ ...currentScheme, schemeCountryId: code });
            setSaveError(null);
          }}
        />

        <div className="relative mb-4">
          <select
            value={currentScheme?.childProcedureMapping ? "true" : "false"}
            onChange={(e) =>
              setCurrentScheme({
                ...currentScheme,
                childProcedureMapping: e.target.value === "true",
              })
            }
            className="w-full h-[70px] px-6 pr-40 border-2 border-solid border-gray-200 rounded-[40px] outline-none font-lufga text-[16px] bg-white appearance-none cursor-pointer"
          >
            <option value="false">False</option>
            <option value="true">True</option>
          </select>
          <img
            src={downIcon}
            alt=""
            className="absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          />
          <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Internal Procedure Mapping
          </span>
        </div>

        {saveError && <p className="text-red-600 text-sm font-lufga mb-4">{saveError}</p>}

        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className="w-full h-[70px] border-2 border-solid border-[#5AC69F] bg-emerald-400 rounded-[40px] font-lufga font-semibold hover:bg-emerald-500 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
        </div>
      </div>

      {stateMappingModal.open && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black/20 backdrop-blur-sm px-4">
          <div className="relative w-full max-w-md bg-white rounded-[28px] shadow-xl px-8 py-10 sm:px-10 sm:py-12">
            <button
              type="button"
              onClick={closeStateMappingModal}
              disabled={stateMappingBusy}
              className="absolute top-6 right-6 bg-transparent border-none p-0 cursor-pointer disabled:opacity-50"
              aria-label="Close"
            >
              <img src={closeIcon} alt="" className="w-5 h-5" />
            </button>
            <p className="text-center font-lufga font-medium text-[#1B1B1B] text-lg sm:text-xl leading-snug mt-2 pr-8">
              {stateMappingModal.action === "activate"
                ? "Are you sure, you want to activate the State?"
                : "Are you sure, you want to inactive the State?"}
            </p>
            {stateMappingModal.stateName && (
              <p className="text-center font-lufga text-sm text-gray-600 mt-3">
                {stateMappingModal.stateName}
              </p>
            )}
            <div className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 mt-10">
              <button
                type="button"
                onClick={closeStateMappingModal}
                disabled={stateMappingBusy}
                className="rounded-full px-8 py-3.5 min-h-[52px] border-2 border-solid border-[#E0E0E0] bg-white hover:bg-gray-50 font-lufga font-semibold text-[#1B1B1B] transition-opacity disabled:opacity-50"
              >
                No
              </button>
              <button
                type="button"
                onClick={handleConfirmStateMapping}
                disabled={stateMappingBusy}
                className="rounded-full px-8 py-3.5 min-h-[52px] border-2 border-solid border-[#82D1B1] bg-[#82D1B1] hover:opacity-95 font-lufga font-semibold text-[#1B1B1B] transition-opacity disabled:opacity-50"
              >
                {stateMappingBusy
                  ? "…"
                  : stateMappingModal.action === "activate"
                    ? "Yes, Activate State"
                    : "Yes, Inactive State"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return createPortal(modal, document.body);
}

function CountryDropdown({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const ref = useRef(null);
  const searchRef = useRef(null);

  const countries = Country.getAllCountries();

  const filtered = search
    ? countries.filter(
        (c) =>
          c.isoCode.toLowerCase().includes(search.toLowerCase()) ||
          c.name.toLowerCase().includes(search.toLowerCase())
      )
    : countries;

  const selectedCountry = countries.find((c) => c.isoCode === value);
  const displayValue = selectedCountry
    ? `${selectedCountry.isoCode} (${selectedCountry.name})`
    : "";

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  useEffect(() => {
    if (open && searchRef.current) setTimeout(() => searchRef.current?.focus(), 100);
    if (!open) {
      setSearch("");
      setHighlightedIndex(-1);
    }
  }, [open]);

  const handleSelect = (code) => {
    onChange(code);
    setOpen(false);
    setSearch("");
  };

  return (
    <div ref={ref} className="relative mb-4">
      <div
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 w-full h-[70px] px-6 pr-36 border-2 border-solid border-gray-200 rounded-[40px] font-lufga text-[16px] bg-white cursor-pointer"
      >
        <span className={`flex-1 truncate ${displayValue ? "text-[#1B1B1B]" : "text-gray-400"}`}>
          {displayValue || "Country Code"}
        </span>
        {displayValue && (
          <span className="absolute right-12 top-1/2 -translate-y-1/2 font-lufga text-[13px] text-gray-400 pointer-events-none">
            Country Code
          </span>
        )}
        <img
          src={downIcon}
          alt=""
          className={`absolute right-6 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>

      {open && (
        <div className="absolute z-50 left-0 right-0 mt-2 bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="sticky top-0 bg-white border-b border-gray-200 z-10 px-4 py-3">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
              <img src={searchIcon} alt="" className="w-4 h-4 flex-shrink-0 opacity-50" />
              <input
                ref={searchRef}
                type="text"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setHighlightedIndex(-1);
                }}
                onKeyDown={(e) => {
                  if (filtered.length === 0) return;
                  if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev < filtered.length - 1 ? prev + 1 : 0));
                  } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : filtered.length - 1));
                  } else if (e.key === "Enter" && highlightedIndex >= 0 && highlightedIndex < filtered.length) {
                    e.preventDefault();
                    handleSelect(filtered[highlightedIndex].isoCode);
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                placeholder="Search country..."
                className="w-full border-none outline-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
              />
            </div>
          </div>
          <div className="max-h-[240px] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-6 py-6 text-center text-gray-500 font-lufga text-sm">No countries found</div>
            ) : (
              filtered.map((c, idx) => {
                const isSelected = c.isoCode === value;
                const isHighlighted = highlightedIndex === idx;
                return (
                  <div
                    key={c.isoCode}
                    onClick={() => handleSelect(c.isoCode)}
                    onMouseEnter={() => setHighlightedIndex(idx)}
                    className={`px-6 py-3 cursor-pointer font-lufga text-sm border-b border-gray-100 last:border-b-0 flex items-center justify-between
                      ${
                        isSelected
                          ? "bg-[#E8F5E9] text-[#2E7D32]"
                          : isHighlighted
                            ? "bg-[#F0F9F4] ring-1 ring-inset ring-[#5AC69F]"
                            : "hover:bg-[#F4FBF8]"
                      }`}
                  >
                    <span>
                      <span className="font-medium">{c.isoCode}</span>
                      <span className="text-gray-500 ml-2">({c.name})</span>
                    </span>
                    {isSelected && <span className="text-[#2E7D32] font-bold">✓</span>}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
